<template>
    <RegularModal ref="modal">
        <template v-slot:title>
            {{ translate("admin_schedule_manage.schedule_conflicts") }}
        </template>
        <template v-slot:body>
            <template v-if="initDone">
                <div class="columns" v-if="view === VIEW_CONFLICTS_NOT_FOUND">
                    <div class="column">
                        <p>{{ translate("admin_schedule_manage.conflicts_not_found") }}</p>
                    </div>
                </div>
                <div class="columns" v-if="view === VIEW_ALL">
                    <div class="column">
                        <div class="box" v-for="competitorDetails in conflicts" :key="competitorDetails.competitor.competitorId">
                            <p class="is-size-5">
                                <Country :only-flag="true" :country-code="competitorDetails.competitor.nationality" />
                                <span class="clarity-mask">
                                    {{ competitorDetails.competitor.firstName }}
                                    {{ competitorDetails.competitor.lastName }}
                                </span>
                            </p>

                            <div v-for="(conflictDetails, index) in competitorDetails.conflictDetails" :key="index">
                                <span class="has-text-weight-bold">{{ conflictDetails.matName }}:</span>
                                <span>{{ conflictDetails.categoryName }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="columns" v-if="view === VIEW_SINGLE">
                    <div class="column">
                        <div class="mb-2 is-size-5">
                            <span class="is-description">{{ translate("admin_schedule_manage.selected_category") }}</span>
                            <p>
                                <span>
                                    {{ conflictsForScheduledCategory.requestedScheduledCategory.categoryName }}
                                </span>
                                <span>
                                    <span class="icon">
                                        <i class="fa fa-clock" />
                                    </span>
                                    <span>
                                        {{ formatStartTimeToEventTimezone(conflictsForScheduledCategory.requestedScheduledCategory.time) }}
                                    </span>
                                </span>
                            </p>
                        </div>
                        <div>
                            <p class="is-size-5 is-description mb-1">
                                {{ translate("admin_schedule_manage.conflicts_with") }}
                            </p>
                            <div
                                class="mb-1"
                                v-for="(conflictedScheduledCategories, index) in conflictsForScheduledCategory.conflicts"
                                :key="index"
                            >
                                <span class="has-text-weight-bold">{{ conflictedScheduledCategories.matName }}:</span>
                                <span>{{ conflictedScheduledCategories.categoryName }}</span>
                                <span class="icon">
                                    <i class="fa fa-clock" />
                                </span>
                                <span>
                                    {{ formatStartTimeToEventTimezone(conflictedScheduledCategories.time) }}
                                </span>
                            </div>
                        </div>
                        <div class="mt-3">
                            <button class="button is-primary" @click="showRelatedMats">
                                <span class="icon">
                                    <i class="fas fa-eye"></i>
                                </span>
                                <span>
                                    {{ translate("admin_schedule_manage.show_related_mats") }}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </template>
            <div class="columns is-centered is-mobile" v-else>
                <LoadingSpinner2 class="has-added-margin" />
            </div>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">{{ translate("common.close") }}</button>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref } from "vue";
    import { translate } from "../../Common/translations";
    import { handleError } from "../../Common/errorHandler";
    import { convertToTimezone } from "../../Common/dateTimeUtils";
    import schedulesApi from "../../Api/schedules";
    import type { ConflictDetailsResult, ScheduledCategoryTime, ConflictsForScheduledCategory } from "../../Api/schedules";
    import { useScheduleMainStore } from "./useScheduleMainStore";
    import { useShowScheduleStore } from "./useShowScheduleStore";
    import RegularModal from "../../Common/RegularModal.vue";
    import Country from "../../Common/Country.vue";
    import LoadingSpinner2 from "../../Common/LoadingSpinner2.vue";

    const VIEW_ALL = "all";
    const VIEW_SINGLE = "single";
    const VIEW_CONFLICTS_NOT_FOUND = "conflicts_not_found";

    // Stores
    const mainStore = useScheduleMainStore();
    const showScheduleStore = useShowScheduleStore();

    // Refs
    const modal = ref<InstanceType<typeof RegularModal> | null>(null);

    // State
    const conflicts = ref<ConflictDetailsResult>([]);
    const conflictsForScheduledCategory = ref<ConflictsForScheduledCategory>({
        requestedScheduledCategory: {},
        conflicts: [],
    });
    const view = ref<string>(VIEW_ALL);
    const initDone = ref(false);

    // Methods
    function close() {
        modal.value?.close();
    }

    function openAll() {
        clearState();
        view.value = VIEW_ALL;
        modal.value?.open();

        return schedulesApi
            .getConflictsForAllCompetitors(mainStore.eventId, mainStore.scheduleId, showScheduleStore.conflictsThreshold)
            .then((conflictsData: ConflictDetailsResult) => {
                conflicts.value = conflictsData;
            })
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    }

    function openCategory(scheduledCategoryId: number) {
        clearState();
        view.value = VIEW_SINGLE;
        modal.value?.open();

        return schedulesApi
            .getConflictsForScheduledCategory(
                mainStore.eventId,
                mainStore.scheduleId,
                scheduledCategoryId,
                showScheduleStore.conflictsThreshold,
            )
            .then((conflictsData: ConflictsForScheduledCategory) => {
                conflictsForScheduledCategory.value = conflictsData;
            })
            .catch(err => {
                if (err.statusCode === 404) {
                    view.value = VIEW_CONFLICTS_NOT_FOUND;
                    return;
                }

                handleError(err);
            })
            .finally(() => {
                initDone.value = true;
            });
    }

    function showRelatedMats() {
        showScheduleStore.matVisibility = {};

        // Requested mat visibility.
        showScheduleStore.setMatVisibility(conflictsForScheduledCategory.value.requestedScheduledCategory.matId!, true);

        // And all related mats visibility.
        conflictsForScheduledCategory.value.conflicts.forEach(({ matId }) => {
            showScheduleStore.setMatVisibility(matId, true);
        });

        close();
    }

    function clearState() {
        initDone.value = false;
        conflicts.value = [];
        conflictsForScheduledCategory.value = {
            requestedScheduledCategory: {},
            conflicts: [],
        };
    }

    function formatStartTimeToEventTimezone(startTime: ScheduledCategoryTime | undefined): string {
        if (!startTime) {
            return "";
        }

        const targetTimezone = mainStore.eventTimezone;
        const timezonedBeginningTime = convertToTimezone(startTime.start, targetTimezone, "HH:mm");
        const timezonedEndingTime = convertToTimezone(startTime.end, targetTimezone, "HH:mm");

        return `${timezonedBeginningTime} - ${timezonedEndingTime}`;
    }

    defineExpose({
        openAll,
        openCategory,
    });
</script>

<style scoped></style>
